// src/shared/hooks/useRouteAccess.ts

import { useMemo } from 'react';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import { IUserProfile, IUserCustomer } from '@/domains/user/user.types';
import {
  CUSTOMER_STATUS,
  CUSTOMER_KYC_STATUS,
  isDemoCustomer,
} from '@/shared/types/customer';
import { USER_ROLES } from '../types/user';

const ACCOUNT_ROUTES = new Set<string>([
  NAVBAR_SOURCES.settings,
  NAVBAR_SOURCES.businessDetails,
  NAVBAR_SOURCES.users,
  NAVBAR_SOURCES.bankAccounts,
]);

const FULL_ROUTES = new Set<string>([
  NAVBAR_SOURCES.dashboard,

  NAVBAR_SOURCES.commissions,
  NAVBAR_SOURCES.revenues,
  NAVBAR_SOURCES.invoicesLedger,
  NAVBAR_SOURCES.activity,
  NAVBAR_SOURCES.documents,

  //NAVBAR_SOURCES.fundingRules,
  NAVBAR_SOURCES.commercialTerms,

  ...ACCOUNT_ROUTES,
]);

const KYC_ROUTE = NAVBAR_SOURCES.kyc;

type AccessResult = {
  isRestrictedView: boolean;
  showKYC: boolean;
  landingPath: string;
  canAccess: (path: string) => boolean;
  allowedRoutes: Set<string>;
};

function isBizopsUser(user: IUserProfile | null) {
  return user?.is_bizops === true;
}

export function useRouteAccess(
  user: IUserProfile | null,
  customer: IUserCustomer | null,
): AccessResult {
  return useMemo(() => {
    if (!customer) {
      // Empty customers: dead-end only
      const allowed = new Set<string>([NAVBAR_SOURCES.guest]);
      return {
        isRestrictedView: true,
        showKYC: false,
        landingPath: NAVBAR_SOURCES.guest,
        canAccess: (p) => p === '/' || allowed.has(p),
        allowedRoutes: allowed,
      };
    }

    const full_view =
      customer.status === CUSTOMER_STATUS.suspended ||
      customer.status === CUSTOMER_STATUS.live ||
      customer.status === CUSTOMER_STATUS.demo;
    const showKYC =
      !isDemoCustomer(customer) &&
      customer.kyc_status === CUSTOMER_KYC_STATUS.under_review &&
      customer.role === USER_ROLES.admin;

    const isRestrictedView = isBizopsUser(user) ? false : !full_view;

    let landingPath = NAVBAR_SOURCES.dashboard;
    if (isRestrictedView) {
      landingPath = showKYC
        ? NAVBAR_SOURCES.kyc
        : NAVBAR_SOURCES.businessDetails;
    }

    const allowed = new Set<string>(
      isRestrictedView ? ACCOUNT_ROUTES : FULL_ROUTES,
    );
    if (showKYC) allowed.add(KYC_ROUTE);

    const canAccess = (path: string) => {
      if (path === KYC_ROUTE) return showKYC;
      if (path === '/') return true;
      return allowed.has(path);
    };

    return {
      isRestrictedView,
      showKYC,
      landingPath,
      canAccess,
      allowedRoutes: allowed,
    };
  }, [user, customer]);
}
